import { supabase } from "@/integrations/supabase/client";

const ORDER_ASSETS_BUCKET = "order-assets";
const SIGNED_TTL_SECONDS = 60 * 60; // 1 hour — admin viewing sessions

/**
 * Extract a storage path inside the `order-assets` bucket from a stored
 * value. The value may be:
 *   - A legacy public URL (…/storage/v1/object/public/order-assets/<path>)
 *   - An already-signed URL (…/storage/v1/object/sign/order-assets/<path>?token=…)
 *   - A bare path under the bucket (e.g. "logos/123-foo.png")
 * Returns null if no usable bucket path can be extracted.
 */
function extractOrderAssetPath(raw: string): string | null {
  if (!raw) return null;
  const publicMarker = `/storage/v1/object/public/${ORDER_ASSETS_BUCKET}/`;
  const signMarker = `/storage/v1/object/sign/${ORDER_ASSETS_BUCKET}/`;
  if (raw.includes(publicMarker)) {
    return raw.split(publicMarker)[1]?.split("?")[0] ?? null;
  }
  if (raw.includes(signMarker)) {
    // Already a signed URL — caller can use as-is.
    return null;
  }
  if (!raw.startsWith("http")) return raw;
  return null;
}

/**
 * Resolve a stored asset reference into a URL the browser can fetch.
 * Returns the already-usable URL when the input is already a signed URL,
 * otherwise generates a fresh short-lived signed URL.
 */
export async function resolveOrderAssetUrl(raw: string | null | undefined): Promise<string | null> {
  if (!raw) return null;
  if (raw.includes(`/storage/v1/object/sign/${ORDER_ASSETS_BUCKET}/`)) return raw;
  const path = extractOrderAssetPath(raw);
  if (!path) return raw; // unknown shape — pass through
  const { data, error } = await supabase
    .storage
    .from(ORDER_ASSETS_BUCKET)
    .createSignedUrl(path, SIGNED_TTL_SECONDS);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}